import { CONTRACTS_ENDPOINT, TENANT_SETTINGS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import { Contract } from "../../../../types/contracts";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Typography, Divider } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import { useHasPermission } from "../../../../contexts/auth";
import { ContractEndDateDisplay, formatNumber } from "../../../../utils";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

type ContractBasicInfoProps = {
  previewId?: string | number;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

export const ContractBasicInfo = ({ previewId }: ContractBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(CONTRACTS_ENDPOINT);
  const { get: getTenantSettings } = useCRUD(TENANT_SETTINGS_ENDPOINT);
  const [contract, setContract] = useState<Contract | null>(null);
  const [automationRunDay, setAutomationRunDay] = useState<number | null>(null);
  const [defaultCounterPriorityProviderDisplay, setDefaultCounterPriorityProviderDisplay] = useState<string | null>(null);
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchContract = async () => {
      if (entityId) {
        const response = await get(entityId);
        setContract(response.data);
      }
    };

    const handleContractUpdated = () => {
      fetchContract();
    };

    window.addEventListener("contractUpdated", handleContractUpdated);
    fetchContract();
    return () => {
      window.removeEventListener("contractUpdated", handleContractUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  useEffect(() => {
    const fetchAutomationRunDay = async () => {
      const response = await getTenantSettings("settings");
      setAutomationRunDay(response?.data?.automation_run_day ?? null);
      setDefaultCounterPriorityProviderDisplay(
        response?.data?.equipment_counter_priority_provider_display ?? null
      );
    };

    fetchAutomationRunDay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!contract) return <CustomCircularProgress />;

  return (
    <Box
      sx={basicInfoContainerStyle}
    >
      {/* Header Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {contract.internal_name || contract.number}
        </Typography>
      </Box>

      <Divider />

      {/* Basic Information Section */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {contract.client && (
            <DetailRow label={t("Client Name")}>
              <EntityReferenceLink
                entityType="client"
                entityId={contract.client?.id}
                label={contract.client?.short_name || contract.client?.name || "-"}
                canPreview={canViewClients}
                stopPropagation={false}
              />
            </DetailRow>
          )}
          {contract.internal_name && (
            <DetailRow label={t("Internal name")}>
              <Typography variant="subtitle1">{contract.internal_name}</Typography>
            </DetailRow>
          )}
          {contract.number && (
            <DetailRow label={t("Number")}>
              <Typography variant="subtitle1">{contract.number}</Typography>
            </DetailRow>
          )}
          {contract.numeration_schema && (
            <DetailRow label={t("Numeration schema")}>
              <Typography variant="subtitle1">{contract?.numeration_schema?.name || "-"}</Typography>
            </DetailRow>
          )}
          <DetailRow label={t("Settlement kind")}>
            <Typography variant="subtitle1">
              {contract.settlement_kinds?.join(", ") || "-"}
            </Typography>
          </DetailRow>
          <DetailRow label={t("Has pending settlements")}>
            <Chip
              label={contract.has_pending_settlements ? t("Unsettled") : t("Settled")}
              size="small"
              sx={{
                bgcolor: contract.has_pending_settlements
                  ? "warning.light"
                  : "success.light",
                color: contract.has_pending_settlements
                  ? "warning.contrastText"
                  : "success.contrastText",
                fontWeight: 600,
                "& .MuiChip-label": {
                  color: contract.has_pending_settlements
                    ? "warning.contrastText"
                    : "success.contrastText",
                },
              }}
            />
          </DetailRow>
          <DetailRow label={t("Inflation condition")}>
            <Typography variant="subtitle1">
              {contract.inflation_condition_display?.trim()
                ? `${contract.inflation_condition_display.trim()}${
                    contract.inflation_rate_percent != null
                      ? ` (${contract.inflation_rate_percent}%)`
                      : ""
                  }`
                : "-"}
            </Typography>
          </DetailRow>
          <DetailRow label={t("Settlement Automation")}>
            <Typography variant="subtitle1">
              {contract.automation_level_display?.trim() || t("Manual")}
              {contract.automation_level && automationRunDay
                ? ` ${t("(runs on day {{day}} of each month)", { day: automationRunDay })}`
                : ""}
            </Typography>
          </DetailRow>
          <DetailRow label={t("Counter priority provider")}>
            <Typography variant="subtitle1">
              {contract.counter_priority_provider_display?.trim()
                ? contract.counter_priority_provider_display
                : defaultCounterPriorityProviderDisplay
                ? t("Inherits tenant default ({{provider}})", { provider: defaultCounterPriorityProviderDisplay })
                : t("Inherits tenant default (not set)")}
            </Typography>
          </DetailRow>
          <DetailRow label={t("Values stored as")} noBorder>
            <Typography variant="subtitle1">
              {contract.is_brutto ? t("Gross (Brutto)") : t("Net (Netto)")}
            </Typography>
          </DetailRow>
        </Box>
      </Box>


      {contract.terms && contract.terms.length > 0 && (
        <>
          <Divider />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t("Contract Terms Details")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {contract.terms.map((term) => (
                <Accordion
                  key={term.id}
                  defaultExpanded
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {term.equipments?.map((equipment) => equipment.full_name).filter(Boolean).join(", ") || t("Contract Position")}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <DetailRow label={t("Equipment")}>
                      {term.equipments?.length ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          {term.equipments.map((equipment) => (
                            <EntityReferenceLink
                              key={equipment.id}
                              entityType="equipment"
                              entityId={equipment.id}
                              label={equipment.full_name || "-"}
                              canPreview={canViewEquipment}
                              stopPropagation={false}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="subtitle1">-</Typography>
                      )}
                    </DetailRow>
                    <DetailRow label={t("Kind")}>
                      <Typography variant="subtitle1">{term.kind_display || "-"}</Typography>
                    </DetailRow>
                    <DetailRow label={t("Settlement Cycle")}>
                      <Typography variant="subtitle1">{term.settlement_cycle_display || "-"}</Typography>
                    </DetailRow>
                    <DetailRow label={t("Settlement kind")}>
                      <Typography variant="subtitle1">{term.settlement_kind_display || "-"}</Typography>
                    </DetailRow>
                    <DetailRow label={t("Rent Price")}>
                      <Typography variant="subtitle1">{formatNumber(term.rent_price)} zł</Typography>
                    </DetailRow>
                    <DetailRow label={t("Prices")} noBorder>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {term.prices?.length ? (
                          term.prices.map((price, index) => (
                            <Typography key={index} variant="subtitle1">
                              {price.price ? `${formatNumber(price.price)} zł` : "-"}
                              {price.included_in_rent ? ` (${t("Included in Rent")}: ${price.included_in_rent})` : ""}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="subtitle1">-</Typography>
                        )}
                      </Box>
                    </DetailRow>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        </>
      )}

      {/* Contract Dates Section */}
      <Divider />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Contract Dates")}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {contract.start_date && (
            <Typography variant="subtitle1">
              <strong>{t("Start Date")}:</strong> {contract.start_date}
            </Typography>
          )}
          <Typography variant="subtitle1">
            <strong>{t("End Date")}:</strong>{" "}
            <ContractEndDateDisplay
              endDate={contract.end_date}
              indefiniteAfterEndDate={contract.indefinite_after_end_date}
              t={t}
            />
          </Typography>
          {contract.issuance_date && (
            <Typography variant="subtitle1">
              <strong>{t("Issuance Date")}:</strong> {contract.issuance_date}
            </Typography>
          )}
          {contract.signature_date && (
            <Typography variant="subtitle1">
              <strong>{t("Signature Date")}:</strong> {contract.signature_date}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
